import React, { useState, useEffect, useMemo } from 'react';
import { MapPin, Phone, Mail, Smartphone, Send } from 'lucide-react';
import { SectionHeader } from '../components/site/SectionHeader';
import { SEOHead } from '../components/site/SEOHead';
import { toast } from 'sonner';
import api from '../lib/api';

export default function Contact() {
  const [contact, setContact] = useState(null);
  const [seo, setSeo] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', captcha_answer: '', website: '' });
  const [errors, setErrors] = useState({});

  const captcha = useMemo(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    return { a, b, answer: a + b };
  }, []);

  useEffect(() => {
    Promise.all([
      api.get('/settings/contact'),
      api.get('/seo/contact')
    ]).then(([c, s]) => {
      setContact(c.data);
      setSeo(s.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Ad soyad gerekli';
    if (!form.email.trim()) errs.email = 'E-posta gerekli';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Geçerli bir e-posta giriniz';
    if (!form.message.trim()) errs.message = 'Mesaj gerekli';
    if (!form.captcha_answer) errs.captcha_answer = 'Guvenlik sorusu gerekli';
    else if (parseInt(form.captcha_answer) !== captcha.answer) errs.captcha_answer = 'Yanlış cevap';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await api.post(`/contact?expected_answer=${captcha.answer}`, {
        ...form,
        captcha_answer: parseInt(form.captcha_answer)
      });
      toast.success('Mesajınız başarıyla gonderildi!');
      setForm({ name: '', email: '', phone: '', message: '', captcha_answer: '', website: '' });
      setErrors({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bir hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEOHead title={seo.title} description={seo.description} keywords={seo.keywords} />
      
      {/* Hero Banner */}
      <div className="relative py-16 md:py-24 bg-[#0f1419]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            İletişim
          </h1>
          <div className="mt-4 w-16 h-1 rounded-full bg-[hsl(var(--gold))]" />
        </div>
      </div>

      <section className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-4">
              <SectionHeader title="Bize Ulaşın" />
              <div className="space-y-4">
                {contact?.address && (
                  <ContactInfo icon={<MapPin />} label="Adres" value={contact.address} />
                )}
                {contact?.email && (
                  <ContactInfo icon={<Mail />} label="E-posta" value={contact.email} href={`mailto:${contact.email}`} />
                )}
                {contact?.phone && (
                  <ContactInfo icon={<Phone />} label="Telefon" value={contact.phone} href={`tel:${contact.phone}`} />
                )}
                {contact?.gsm && (
                  <ContactInfo icon={<Smartphone />} label="GSM" value={contact.gsm} href={`tel:${contact.gsm}`} />
                )}
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-8">
              <div className="p-6 md:p-8 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
                <h3 className="font-heading text-xl font-semibold mb-6">İletişim Formu</h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  {/* Honeypot: hidden from real users; bots that fill it are rejected */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                    className="hidden"
                    aria-hidden="true"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      label="Ad Soyad"
                      value={form.name}
                      onChange={v => setForm(f => ({ ...f, name: v }))}
                      error={errors.name}
                      testId="contact-form-name-input"
                      required
                    />
                    <FormField
                      label="E-posta"
                      type="email"
                      value={form.email}
                      onChange={v => setForm(f => ({ ...f, email: v }))}
                      error={errors.email}
                      testId="contact-form-email-input"
                      required
                    />
                    <FormField
                      label="Telefon"
                      value={form.phone}
                      onChange={v => setForm(f => ({ ...f, phone: v }))}
                      testId="contact-form-phone-input"
                    />
                    <FormField
                      label={`Güvenlik Sorusu (${captcha.a} + ${captcha.b})`}
                      type="number"
                      value={form.captcha_answer}
                      onChange={v => setForm(f => ({ ...f, captcha_answer: v }))}
                      error={errors.captcha_answer}
                      testId="contact-form-captcha-input"
                      required
                    />
                  </div>
                  <FormField
                    label="Mesajınız"
                    textarea
                    value={form.message}
                    onChange={v => setForm(f => ({ ...f, message: v }))}
                    error={errors.message}
                    testId="contact-form-message-textarea"
                    required
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] font-medium text-sm hover:bg-[hsl(var(--gold)/0.92)] active:scale-[0.98] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-form-submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Gönderiliyor...
                      </>
                    ) : (
                      <><Send className="w-4 h-4" /> Gönder</>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Map */}
          {contact?.map_iframe && (
            <div className="mt-12 rounded-2xl overflow-hidden border border-border">
              <div dangerouslySetInnerHTML={{ __html: contact.map_iframe }} className="w-full h-[400px] [&>iframe]:w-full [&>iframe]:h-full" />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const ContactInfo = ({ icon, label, value, href }) => (
  <div className="flex items-start gap-3">
    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center shrink-0">
      {React.cloneElement(icon, { className: 'w-4 h-4 text-[hsl(var(--gold))]' })}
    </div>
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {href ? (
        <a href={href} className="text-sm font-medium hover:text-[hsl(var(--gold))] transition-colors">{value}</a>
      ) : (
        <div className="text-sm font-medium">{value}</div>
      )}
    </div>
  </div>
);

const FormField = ({ label, value, onChange, error, testId, type = 'text', textarea, required }) => (
  <div className={textarea ? 'md:col-span-2' : ''}>
    <label className="block text-sm font-medium mb-1.5">
      {label} {required && <span className="text-destructive">*</span>}
    </label>
    {textarea ? (
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={5}
        className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))] focus:ring-offset-2 resize-none"
        data-testid={testId}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))] focus:ring-offset-2"
        data-testid={testId}
      />
    )}
    {error && <p className="text-xs text-destructive mt-1" data-testid="contact-form-error-text">{error}</p>}
  </div>
);
